import { TriangleAlert } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  DatePicker,
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  MoneyInput,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  RadioGroup as TypeRadioGroup,
  cn,
} from '@column/ui';

import { AchAccountsSequence } from '../_components/AchAccountsSequence';
import { RestrictedBadge } from '../_components/RestrictedBadge';
import { CreateTransfer } from '../Add';
import { FormField, RenderFields } from '~/components';
import { UnderlineLink } from '~/elements';
import { useSessionStore } from '~/stores/Session';
import { SAME_DAY_ACH_CUTOFF_LABEL, getDateFormat, isPastSameDayAchCutoff } from '~/util';

const DEFINITION_LIST_CLASS =
  '[&_dd]:mb-6 [&_dd]:text-sm [&_dt]:mb-1 [&_dt]:text-base [&_dt]:font-medium [&_dt]:text-foreground';

const parseDate = (value: string): Date | undefined => {
  const [year, month, day] = value.split('-').map(Number);
  return year && month && day ? new Date(year, month - 1, day) : undefined;
};

export const EntryClassCodeDefinitions = (
  <>
    <dl className={DEFINITION_LIST_CLASS}>
      <dd>
        A Standard Entry Class (SEC) code is a three-character code used to identify various properties of ACH
        transfers. Column supports all SEC codes for incoming ACH transfers. However, for outgoing ACH transfers that
        are initiated via the dashboard, only the following codes are supported:
      </dd>
      <dt>CCD</dt>
      <dd>
        Corporate Credit or Debit: Entry initiated by an Organization to transfer funds to or from an account of that
        Organization or another Organization.
      </dd>
      <dt>CIE</dt>
      <dd>
        Customer Initiated Entry: Credit Entry initiated by or on behalf of the holder of a Consumer Account to transfer
        funds to the account of the Receiver.
      </dd>
      <dt>CTX</dt>
      <dd>
        Corporate Trade Exchange: Entry initiated by an Organization to transfer funds to or from the account of that
        Organization or another Organization that permits the inclusion of payment-related remittance information in
        ANSI or UN/EDIFACT syntax.
      </dd>
      <dt>PPD</dt>
      <dd>
        Prearranged Payment and Deposit: Entry initiated by an Organization based on a standing or a Single Entry
        authorization from a Receiver to transfer funds to or from a Consumer Account of the Receiver.
      </dd>
      <dt>TEL</dt>
      <dd>
        Telephone-Initiated Entry: Debit initiated by an Originator pursuant to an oral authorization obtained over the
        telephone to transfer funds from a Consumer Account of the Receiver.
      </dd>
      <dt>WEB</dt>
      <dd>
        Internet-Initiated/Mobile Entry: (1) a debit Entry initiated by an Originator to a Consumer Account of the
        Receiver based on (a) an authorization that is communicated, other than by an oral communication, from the
        Receiver to the Originator via the Internet or a Wireless Network, or (b) any form of authorization if the
        Receiver's instruction for the initiation of the individual debit Entry is designed by the Originator to be
        communicated, other than by an oral communication, to the Originator via a Wireless Network; or (2) a credit
        Entry initiated by or on behalf of the holder of a Consumer Account that is intended for the Consumer Account of
        a Receiver, regardless of whether the authorization of such Entry is communicated via the Internet or Wireless
        Network.
      </dd>
    </dl>

    <UnderlineLink href="https://docs.column.com/ach/types#ach-standard-entry-class-codes" newTab>
      Learn more about Entry Class Codes
    </UnderlineLink>
  </>
);

export const IdempotencyKeyDefinitions = (
  <>
    <dl className={DEFINITION_LIST_CLASS}>
      <dd>
        Column automatically generates idempotency keys for safely retrying transfers without accidentally duplicating
        the result. Custom idempotency keys can be set by clearing the default value and replacing it with your own key.
        This can be helpful when your API call fails and you aren't sure if Column received the request. Idempotency
        enables you to retry a request without worrying it will create a duplicate transaction.
      </dd>
      <dd>
        In order to perform an idempotent request, provide any unique string up to 255 characters long and is case
        sensitive. Column reserves the right to expire idempotency keys after 30 days.
      </dd>
    </dl>
    <UnderlineLink href="https://docs.column.com/working-with-the-api/idempotency" newTab>
      Learn more about Idempotency Keys
    </UnderlineLink>
  </>
);

const AchContent: FC<ComponentLoaderTypeProps> = ({ onLoaded }) => {
  const [isSameDay, setIsSameDay] = useState<boolean>(false);
  // Local midnight, so the date comparisons don't disable tomorrow itself.
  const tomorrowDate = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    return date;
  }, []);
  const tomorrow = useMemo(() => getDateFormat(tomorrowDate), [tomorrowDate]);
  const [cutoffPassed, setCutoffPassed] = useState<boolean>(() => isPastSameDayAchCutoff());
  useEffect(() => {
    const interval = setInterval(() => setCutoffPassed(isPastSameDayAchCutoff()), 30_000);
    return () => clearInterval(interval);
  }, []);
  const showSameDayWarning = isSameDay && cutoffPassed;
  const { isAuthorized } = useSessionStore();

  useEffect(() => {
    onLoaded();
  }, []);

  const isDebitAuthorized = useMemo(
    () =>
      isAuthorized({
        permission: 'achDebits',
        permissionLevel: 'write',
      }),
    [isAuthorized]
  );
  const isCreditAuthorized = useMemo(
    () =>
      isAuthorized({
        permission: 'achCredits',
        permissionLevel: 'write',
      }),
    [isAuthorized]
  );

  return (
    <>
      {RenderFields<CreateTransfer, 'ach'>({
        prefix: 'ach',
        sections: [
          {
            headline: 'Information',
            fields: [
              {
                id: 'amount',
                label: 'Amount',
                rules: { required: true, validate: (v) => Number(v) > 0 },
                children: ({ value, onChange, onBlur }, { error }) => (
                  <MoneyInput
                    currency="USD"
                    value={value === undefined || value === null || value === '' ? undefined : Number(value)}
                    onValueChange={onChange}
                    onBlur={onBlur}
                    aria-invalid={!!error}
                  />
                ),
              },
              {
                id: 'type',
                label: 'Type',
                description: 'Select between ACH Debit or ACH Credit.',
                rules: { required: true },
                defaultValue: isCreditAuthorized ? 'CREDIT' : 'DEBIT',
                children: ({ value, onChange }) => (
                  <TypeRadioGroup value={String(value ?? '')} onValueChange={onChange}>
                    <FieldGroup variant="outline" orientation="responsive">
                      <Field
                        orientation="horizontal"
                        className="relative"
                        data-disabled={!isCreditAuthorized || undefined}
                      >
                        <RadioGroupItem value="CREDIT" id="ach-type-credit" disabled={!isCreditAuthorized} />
                        <FieldContent>
                          <FieldLabel htmlFor="ach-type-credit">Credit</FieldLabel>
                          <FieldDescription className="text-xs text-pretty">
                            Push money from your bank account to a counterparty.
                          </FieldDescription>
                        </FieldContent>
                        {!isCreditAuthorized && (
                          <RestrictedBadge content={'You do not have permission to use ACH Credits.'} />
                        )}
                      </Field>
                      <Field
                        orientation="horizontal"
                        className="relative"
                        data-disabled={!isDebitAuthorized || undefined}
                      >
                        <RadioGroupItem value="DEBIT" id="ach-type-debit" disabled={!isDebitAuthorized} />
                        <FieldContent>
                          <FieldLabel htmlFor="ach-type-debit">Debit</FieldLabel>
                          <FieldDescription className="text-xs text-pretty">
                            Pull money from a counterparty to your bank account.
                          </FieldDescription>
                        </FieldContent>
                        {!isDebitAuthorized && (
                          <RestrictedBadge content={'You do not have permission to use ACH Debits.'} />
                        )}
                      </Field>
                    </FieldGroup>
                  </TypeRadioGroup>
                ),
              },
              {
                content: <AchAccountsSequence />,
              },
              {
                content: (
                  <>
                    <FormField<CreateTransfer>
                      id="ach.sameDay"
                      label="Same Day"
                      defaultValue={false}
                      tooltip="Enable to send this as a same-day ACH transfer. When enabled, the transfer settles the same business day and takes precedence over the effective date."
                      description={
                        <div
                          className={cn(
                            'grid transition-[grid-template-rows] duration-200 ease-in-out',
                            showSameDayWarning ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
                          )}
                        >
                          <div className="min-h-0 overflow-hidden">
                            <span className="text-pretty @tablet/form-field:[&>span]:block">
                              <span>Must be sent on a business day</span>{' '}
                              <span>before {SAME_DAY_ACH_CUTOFF_LABEL}</span>
                            </span>
                          </div>
                        </div>
                      }
                    >
                      {({ value, onChange }) => (
                        <div className="flex flex-col items-start @tablet/form-field:mt-3">
                          <Switch
                            checked={Boolean(value)}
                            onCheckedChange={(checked) => {
                              onChange(checked);
                              setIsSameDay(checked);
                            }}
                          />
                          <Accordion type="single" value={showSameDayWarning ? 'warning' : ''}>
                            <AccordionItem value="warning" className="border-b-0">
                              <AccordionContent className="pt-2 pb-0">
                                <span className="inline-flex items-start gap-1.5 text-xs leading-4 text-muted-foreground">
                                  <TriangleAlert className="mt-px size-3.5 shrink-0 text-orange-500" />
                                  The deadline to guarantee same-day settlement has passed. This transfer will settle
                                  the following business day by 5:30 AM PT.
                                </span>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      )}
                    </FormField>
                    <Accordion type="single" value={isSameDay ? '' : 'effectiveDate'}>
                      {/* Widened so the accordion's clipping doesn't crop the date picker's focus ring. */}
                      <AccordionItem value="effectiveDate" className="-mx-1 -mb-1 border-b-0">
                        <AccordionContent className="px-1 pt-6 pb-1">
                          <FormField<CreateTransfer>
                            id="ach.effectiveDate"
                            label="Effective Date"
                            defaultValue={tomorrow}
                            tooltip="Defaults to tomorrow. Pick a later date to schedule the transfer, or enable Same Day to send it today."
                          >
                            {({ value, onChange }, { error }) => {
                              const currentValue = value ? String(value) : tomorrow;
                              return (
                                <DatePicker
                                  value={parseDate(currentValue)}
                                  onValueChange={(next) => onChange(next ? getDateFormat(next) : tomorrow)}
                                  disabledDays={{ before: tomorrowDate }}
                                  defaultDate={tomorrowDate}
                                  aria-invalid={!!error}
                                  trailing={
                                    currentValue === tomorrow ? (
                                      <span className="-my-[3px] inline-flex items-center rounded-md px-1.5 py-[3px] text-[11px] leading-none font-medium whitespace-nowrap text-muted-foreground shadow-[inset_0_0_0_1px_var(--color-border)] select-none">
                                        Tomorrow
                                      </span>
                                    ) : undefined
                                  }
                                />
                              );
                            }}
                          </FormField>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </>
                ),
              },
              {
                id: 'entryClassCode',
                label: 'Entry Class Code',
                defaultValue: 'CCD',
                tooltip: {
                  headline: 'Entry Class Code Definitions',
                  content: EntryClassCodeDefinitions,
                },
                rules: {
                  required: true,
                },
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Select
                    value={String(value ?? '')}
                    onValueChange={onChange}
                    onOpenChange={(open) => !open && onBlur()}
                  >
                    <SelectTrigger size="lg" className="w-full" aria-invalid={!!error}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['CCD', 'CIE', 'CTX', 'PPD', 'TEL', 'WEB'].map((code) => (
                        <SelectItem key={code} value={code} data-cy={`dropdown-option-${code}`}>
                          {code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ),
              },
              {
                id: 'description',
                label: 'Description',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 255,
                    message: 'Description cannot exceed 255 characters.',
                  },
                },
                tooltip:
                  'Optional field to add a description of the transfer for your internal reference and is not sent externally. Maximum 255 characters.',
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Input
                    placeholder="Description"
                    aria-invalid={!!error}
                    value={String(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
            ],
          },
          {
            headline: 'Company',
            fields: [
              {
                id: 'companyName',
                label: 'Name',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 16,
                    message: 'Company name cannot exceed 16 characters.',
                  },
                },
                tooltip:
                  'Optional field to add the name of the company which initiated the ACH transfer. Maximum 16 characters.',
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Input
                    placeholder="Company Name"
                    aria-invalid={!!error}
                    value={String(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
              {
                id: 'companyEntryDescription',
                label: 'Entry Description',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 10,
                    message: 'Entry description cannot exceed 10 characters.',
                  },
                },
                tooltip:
                  'Optional field to provide the Receiver with a description of the purpose of the transfer. Default value: "PAYMENT". Maximum 10 characters.',
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Input
                    placeholder="Company Entry Description"
                    aria-invalid={!!error}
                    value={String(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
              {
                id: 'companyDiscretionaryData',
                label: 'Discretionary Data',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 20,
                    message: 'Discretionary data cannot exceed 20 characters.',
                  },
                },
                tooltip:
                  'Optional field to include codes (one or more), of significance only to you, to enable specialized handling of the transfer. There is no standardized interpretation for the value of the field. Maximum 20 characters.',
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Input
                    placeholder="Company Discretionary Data"
                    aria-invalid={!!error}
                    value={String(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
            ],
          },
          {
            headline: 'Receiver',
            fields: [
              {
                id: 'receiverName',
                label: 'Name',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 22,
                    message: 'Receiver name cannot exceed 22 characters.',
                  },
                },
                tooltip:
                  'Optional field to add the name of the receiver, usually the name on the bank account. Maximum 22 characters.',
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Input
                    placeholder="Receiver Name"
                    aria-invalid={!!error}
                    value={String(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
              {
                id: 'receiverId',
                label: 'ID',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 15,
                    message: 'Receiver ID cannot exceed 15 characters.',
                  },
                },
                tooltip:
                  'Optional field to add an accounting reference number by which the Receiver is known to the Originator. It is included for further identification and for descriptive purposes. For example, it may be the number shown on an invoice, statement, billhead, notice, or other communication as the reference for the Receiver to update account receivable records. Maximum 15 characters.',
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Input
                    placeholder="Receiver ID"
                    aria-invalid={!!error}
                    value={String(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
            ],
          },
          {
            headline: 'Advanced',
            isCollapsed: true,
            fields: [
              {
                id: 'allowOverdraft',
                label: 'Allow Overdraft',
                defaultValue: false,
                tooltip:
                  'Turning this on allows an outgoing transfer to cause the selected account to go negative. (Only eligible on overdraftable accounts.)',
                children: ({ value, onChange }) => (
                  <div className="flex @tablet/form-field:mt-3">
                    <Switch aria-label="Allow Overdraft" checked={Boolean(value)} onCheckedChange={onChange} />
                  </div>
                ),
              },
              {
                id: 'paymentRelatedInfo',
                label: 'Payment Related Info',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 80,
                    message: 'Payment related info cannot exceed 80 characters.',
                  },
                },
                tooltip:
                  'Optional field to add payment instructions to the receiving depository financial institution (RDFI). Note, the visibility of this information to the end recipient can vary.',
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Input
                    placeholder="Payment Related Info"
                    aria-invalid={!!error}
                    value={String(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
              },
              {
                id: 'idempotencyKey',
                label: 'Idempotency Key',
                defaultValue: '',
                rules: {
                  maxLength: {
                    value: 255,
                    message: 'Idempotency key cannot exceed 255 characters.',
                  },
                },
                children: ({ value, onChange, onBlur }, { error }) => (
                  <Input
                    placeholder="Idempotency Key"
                    aria-invalid={!!error}
                    value={String(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                ),
                tooltip: {
                  headline: 'Idempotency Keys',
                  content: IdempotencyKeyDefinitions,
                },
              },
            ],
          },
        ],
      })}
    </>
  );
};

export default AchContent;
